import React from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import Button from '@components/atoms/button/button';
import AlertsService from '@services/alerts-service';
import logger from '@helpers/utils/logger/client';
import useProductQuery from '@hooks/product/use-product-query';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import useUser from '@hooks/user/use-user';
import { useCtaProductContext } from '../context';
import styles from '../CTA-product.module.scss';

interface VacationNotificationButtonProps {
  holidaysEndAlertError: string;
  setHolidaysEndAlertError: (arg: string) => void;
}

const VacationNotificationButton: React.FC<VacationNotificationButtonProps> = (props): React.JSX.Element => {
  const { holidaysEndAlertId, sellerBackHolidayAlerts, setSellerBackHolidayAlerts, setHolidaysEndAlertId } =
    useCtaProductContext();

  const { t } = useTranslation();
  const { user } = useUser();
  const { product } = useProductQuery();
  const { isRecirculation } = usePdpLayout();

  const { holidaysEndAlertError, setHolidaysEndAlertError } = props;

  const errorHandler = (err) => {
    logger.error(err, 'Error occurred during creating an alert');
    // TODO: Error string is not translatable:
    // Either we pass language code to backend,
    // or we would need to map it by translation code in translation files
    setHolidaysEndAlertError(err?.response?.data?.errors?.detail);

    // Need to define the proper UI/UX, until then let's use timeout to hide the error to retry the create
    setTimeout(() => setHolidaysEndAlertError(''), 5000);
  };

  const toggleHolidaysEndAlert = async (): Promise<void> => {
    if (holidaysEndAlertId) {
      try {
        await AlertsService.deleteAlert(holidaysEndAlertId, 'alert_sellerHoliday');
        setHolidaysEndAlertId(null);

        const newSellerBackHolidayAlerts = sellerBackHolidayAlerts.filter((item) => {
          const propertyNames = Object.getOwnPropertyNames(item);

          return propertyNames[0] !== product.id;
        });

        setSellerBackHolidayAlerts(newSellerBackHolidayAlerts);
        localStorage.setItem('sellerBackHolidayAlerts', JSON.stringify(newSellerBackHolidayAlerts));
      } catch (err) {
        errorHandler(err?.detail);
      }
    } else {
      try {
        const alert = await AlertsService.createAlert({
          category: 'sellerBackHoliday',
          criteria: [
            {
              key: 'idProduct',
              value: [product.id],
            },
          ],
        });

        if (alert?.data?.data?.id) {
          const newSellerBackHolidayAlerts = [...sellerBackHolidayAlerts, { [product.id]: alert.data.data.id }];

          setSellerBackHolidayAlerts(newSellerBackHolidayAlerts);
          localStorage.setItem('sellerBackHolidayAlerts', JSON.stringify(newSellerBackHolidayAlerts));
          setHolidaysEndAlertId(alert.data.data.id);
        }
      } catch (err) {
        errorHandler(err);
      }
    }
  };

  return (
    <Button
      data-cy="vacation-notification-button"
      variant="secondary"
      size={isRecirculation ? 'medium' : 'large'}
      fullWidth
      className={clsx(
        styles.CTAButtonsBlock__button,
        styles['CTAButtonsBlock__button--alert'],
        isRecirculation && 'vc-btn--large--md-up',
        !!holidaysEndAlertId && styles['CTAButtonsBlock__button--alertCreated'],
      )}
      disabled={!user || !!holidaysEndAlertError}
      onClick={toggleHolidaysEndAlert}
    >
      {holidaysEndAlertId ? (
        <>
          <Icon
            name="checkmark"
            className={styles.CTAButtonsBlock__buttonIcon}
            ariaHidden
          />
          <span>{t('PRODUCT.DETAILS.CTA.ALERT_CREATED')}</span>
        </>
      ) : (
        <span>{t('PRODUCT.DETAILS.CTA.CREATE_AN_ALERT')}</span>
      )}
    </Button>
  );
};

export default VacationNotificationButton;
